import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { HttpClientService } from 'src/app/@shared/services/http-client/http-client.service';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { TariffProfile } from '../interfaces/route-planning/gtfs/tariff-profile.interface';
import { environment } from 'src/environments/environment';
import { HttpHandler, HttpParams } from '@angular/common/http';
import { IntercityLines } from '../interfaces/route-planning/gtfs/intercity-lines.interface';
import {
  GetIntercityLinesRequest,
  RouteStops,
  StopFullData,
  StopMessagesForDayResponse,
  StopTimesAtStopResponse,
  StopTimesWithRoute,
} from '../interfaces/route-planning/service/@schedule-service.interface';
import { Route } from '../interfaces/route-planning/gtfs/route.interface';
import { Stop } from '../interfaces/route-planning/gtfs/stop.interface';
import { SpecialMessage } from '../interfaces/route-planning/gtfs/special-messages/special-message.interface';
import { Coordinate } from '../interfaces/location/coordinate.interface';
import { Price } from '../interfaces/route-planning/price.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';

@Injectable({
  providedIn: 'root',
})
export class ScheduleRepository {
  constructor(
    private readonly httpClientService: HttpClientService,
    httpHandler: HttpHandler,
    languageService: LanguageService
  ) {
    this.httpClientService = new HttpClientService(httpHandler, languageService);
    this.httpClientService.apiUrl = environment.schedule_service_url;
  }

  getTariffProfiles(): Observable<ServiceResponse<TariffProfile[]>> {
    return this.httpClientService.mot_get('TariffProfiles');
  }

  getInterCityLines(
    model: GetIntercityLinesRequest
  ): Observable<ServiceResponse<IntercityLines[]>> {
    return this.httpClientService.mot_get(
      'Routes/GetIntercityLines',
      HttpClientService.objectToQueryParams(model)
    );
  }

  searchLine(lineTerm: string): Observable<ServiceResponse<Route[]>> {
    return this.httpClientService.mot_get(
      'Routes/AutoComplete',
      new HttpParams().set('searchTerm', lineTerm)
    );
  }

  searchStation(stationTerm: string): Observable<ServiceResponse<Stop[]>> {
    return this.httpClientService.mot_get(
      'Stops/AutoComplete',
      new HttpParams().set('searchTerm', stationTerm)
    );
  }

  getStopFullData(stopCode: number): Observable<ServiceResponse<StopFullData>> {
    return this.httpClientService.mot_get(
      'Stops/GetStopFullData',
      new HttpParams().set('stopCode', stopCode)
    );
  }

  getRouteFullData(routeId: number): Observable<ServiceResponse<Route>> {
    return this.httpClientService.mot_get(
      'Routes/GetRouteFullData',
      new HttpParams().set('routeId', routeId)
    );
  }

  getStopByCode(stopcode: number): Observable<ServiceResponse<Stop>> {
    return this.httpClientService.mot_get(
      'Stops/GetStopByCode',
      new HttpParams().set('stopcode', stopcode)
    );
  }

  getRouteStops(
    routeId: number,
    oppositeDirectionId: number,
    date?: Date
  ): Observable<
    ServiceResponse<{ route: RouteStops; oppositeDirectionRoute: RouteStops }>
  > {
    let params = new HttpParams().set('routeId', routeId);

    if (oppositeDirectionId !== null && oppositeDirectionId !== undefined) {
      params = params.set('oppositeDirectionRouteId', oppositeDirectionId);
    }

    if (date !== null && date !== undefined) {
      params = params.set('date', date.toISOString());
    }

    return this.httpClientService.mot_get('Routes/GetRouteStops', params);
  }

  getRoutePrice(model: {
    routeId: number;
    startingStopId: number;
    endingStopId: number;
  }): Observable<ServiceResponse<Price[]>> {
    return this.httpClientService.mot_get('Routes/GetRoutePrice', HttpClientService.objectToQueryParams(model))
  }

  getAllTrainStations(): Observable<ServiceResponse<Stop[]>> {
    return this.httpClientService.mot_get(
      'Stops/GetStopsByAgency',
      new HttpParams().set('agencyId', 2)
    );
  }

  getSpecialMessageByRouteDesc(
    routeDesc: string
  ): Observable<ServiceResponse<SpecialMessage[]>> {
    return this.httpClientService.mot_get(
      'Routes/GetSpecialMessageByRouteDesc',
      new HttpParams().set('routeDesc', routeDesc)
    );
  }

  GetSpecialMessageByManyRouteDesc(
    routeDescs: string[]
  ): Observable<ServiceResponse<SpecialMessage[]>> {
    let params = new HttpParams();
    routeDescs.forEach((desc) => {
      params = params.append('routeDescs', desc);
    });
    return this.httpClientService.mot_get(
      'Routes/GetSpecialMessageByManyRouteDesc',
      params
    );
  }

  getSpecialMessageByStopCodes(
    stopCodes: string[],
    dayPassingAtStop?: number,
    timePassingAtStop?: string
  ): Observable<
    ServiceResponse<{ stopId: number; specialMessages: SpecialMessage[] }[]>
  > {
    let params = new HttpParams();
    stopCodes.forEach((code) => {
      params = params.append('stopCodes', code);
    });
    if (dayPassingAtStop !== undefined && dayPassingAtStop !== null) {
      params = params.set('dayPassingAtStop', dayPassingAtStop);
    }
    if (timePassingAtStop !== undefined && timePassingAtStop !== null) {
      params = params.set('timePassingAtStop', timePassingAtStop);
    }
    return this.httpClientService.mot_get(
      'Stops/GetSpecialMessagesByManyStopCodes',
      params
    );
  }

  getStopsCloseToLocation(coordinates: Coordinate): Observable<ServiceResponse<StopFullData[]>> {
    return this.httpClientService.mot_get(
      'Stops/GetStopsCloseToLocation',
      new HttpParams().set('lat', coordinates.lat).set('lng', coordinates.lng),
    );
  }

  getClosestStop(coordinates: Coordinate): Observable<ServiceResponse<StopFullData>> {
    return this.httpClientService.mot_get(
      'Stops/GetclosestStop',
      new HttpParams().set('lat', coordinates.lat).set('lng', coordinates.lng),
    );
  }

  getRouteLocations(routeId: number): Observable<ServiceResponse<Coordinate[]>> {
    return this.httpClientService.mot_get(
      'Routes/GetRouteLocations',
      new HttpParams().set('routeId', routeId)
    );
  }

  getNextRouteLocation(routeDesc: string, stopCode: string): Observable<ServiceResponse<Coordinate>> {
    return this.httpClientService.mot_get(
      'Routes/GetNextRouteLocation',
      new HttpParams().set('routeDesc', routeDesc).set('stopCode', stopCode)
    );
  }

  getRoutePolylineByRouteDescStopAndTime(
    routeDesc: string,
    stopCode: string,
    time: string
  ): Observable<ServiceResponse<string>> {
    return this.httpClientService.mot_get(
      'Routes/GetRoutePolylineByRouteDescStopAndTime',
      new HttpParams()
        .set('routeDesc', routeDesc)
        .set('stopCode', stopCode)
        .set('time', time)
    );
  }

  getStopTimesAtStop(stopCode: number, day: Date): Observable<ServiceResponse<StopTimesAtStopResponse>> {
    return this.httpClientService.mot_get(
      'Stops/RefreshStopTimesAtStop',
      new HttpParams().set('stopCode', stopCode).set('day', day.toISOString())
    );
  }

  getStopMessagesForTheDay(
    stopCode: number,
    day: Date
  ): Observable<ServiceResponse<StopMessagesForDayResponse>> {
    return this.httpClientService.mot_get(
      'Stops/GetSpecialMessagesByStopAndDay',
      new HttpParams().set('stopCode', stopCode).set('day', day.toISOString())
    );
  }

  checkIfRouteHasAlternatives(routeId: number): Observable<ServiceResponse<boolean>> {
    return this.httpClientService.mot_get(
      'Routes/IsRouteHasAlternatives',
      new HttpParams().set('routeId', routeId)
    );
  }

  getTripShapePolyline(tripId: string): Observable<ServiceResponse<string>> {
    return this.httpClientService.mot_get(
      'Routes/GetTripShapePolyline',
      new HttpParams().set('tripId', tripId)
    );
  }
}
